/**
 * PENALTY SHOOTOUT (§5.1) — "penality if draw" (owner, 2026-08-16).
 *
 * `MatchPhase.PenaltyShootout` has existed in the enum since the phase list was
 * written, and NOTHING implemented it: the HUD already renders "SHOOTOUT" and
 * the audio bed already treats it as live play, but no code path could ever set
 * it, so a level match simply stopped at FullTime. This is that phase.
 *
 * ── How it runs ─────────────────────────────────────────────────────────────
 *
 * The shootout does not re-implement a penalty. Each kick IS the existing
 * `MatchPhase.Penalty` restart — same Law-14 staging, same taker selection
 * (`pickRestartTaker`'s Penalty branch takes the best finisher still on the
 * pitch), same AI, same human controls. This module is the bookkeeping around
 * it: whose turn, how many are left, whether it is already decided, and what
 * happens when an attempt ends.
 *
 * `shootoutActive` is what tells the rest of the rules they are inside one. Two
 * paths in `ball-out.ts` read it, because inside a shootout a ball crossing a
 * line means something different:
 *   • a goal does NOT touch scoreA/scoreB (the match ended level and stays
 *     level in the record) and does NOT kick off — it is a shootout goal;
 *   • a ball out of play anywhere ends the attempt instead of awarding a
 *     corner, a goal kick or a throw-in.
 *
 * ── When an attempt ends ────────────────────────────────────────────────────
 *
 * Real football's rule is that the kick is over the moment the ball stops
 * moving, goes out of play, or the referee stops play — a taker gets exactly
 * one contact. Here an attempt ends on the first of:
 *   1. GOAL — the ball fully crossed the line between the posts.
 *   2. The ball left the field of play.
 *   3. The keeper has it (a save he held).
 *   4. The ball has come to rest, or been kicked back out by the keeper
 *      without going in — the rebound cases.
 *   5. SHOOTOUT_ATTEMPT_MAX_S, so nothing can wedge the match open forever.
 *
 * ── Order and stopping early ────────────────────────────────────────────────
 *
 * Five each, alternating, then sudden death in pairs. Crucially the shootout
 * stops the moment it is DECIDED — 3-0 after four kicks each is over, and
 * making the losing side take two dead kicks is the kind of detail whose
 * absence reads as amateur. `remainingKicks` / `isDecided` are that check, and
 * they run before every kick including the first of a sudden-death pair.
 *
 * Everything here is integer bookkeeping on `MatchState` and therefore hashed
 * and deterministic; the only randomness is the opening coin toss, which is
 * drawn from the sim RNG (§15.3) like every other roll.
 */

import {
  MatchPhase,
  RestartStage,
  MAX_PLAYERS,
  type StepResult,
  type WorldState,
} from '../types.js';
import { pcg32Float } from '../../math/pcg32.js';
import {
  PENALTY_SPOT_DISTANCE,
  SHOOTOUT_ATTEMPT_MAX_S,
  SHOOTOUT_BALL_REST_SPEED,
  SHOOTOUT_INTERVAL_S,
  SHOOTOUT_MAX_KICKS,
  SHOOTOUT_OPENING_PAUSE_S,
  SHOOTOUT_REGULATION_KICKS,
  SIMULATION_TICK_RATE,
} from '../constants.js';
import { beginRestart } from './fsm.js';

/** Kicks this team has taken. */
function kicksOf(state: WorldState, team: number): number {
  return team === 0 ? state.match.shootoutKicksA : state.match.shootoutKicksB;
}

/** Shootout goals this team has scored. */
export function shootoutGoalsOf(state: WorldState, team: number): number {
  return team === 0 ? state.match.shootoutGoalsA : state.match.shootoutGoalsB;
}

/**
 * How many kicks of the regulation five this team has left. Zero once both are
 * through the regulation round — sudden death is counted in pairs instead.
 */
function remainingKicks(state: WorldState, team: number): number {
  return Math.max(0, SHOOTOUT_REGULATION_KICKS - kicksOf(state, team));
}

/**
 * Is the result already beyond reach? — the "no dead kicks" rule.
 *
 * During the regulation five, a side is out of it when its own remaining kicks
 * cannot close the gap. In sudden death the pair is complete and unequal.
 */
export function shootoutDecided(state: WorldState): boolean {
  const m = state.match;
  const kA = m.shootoutKicksA;
  const kB = m.shootoutKicksB;
  const gA = m.shootoutGoalsA;
  const gB = m.shootoutGoalsB;

  if (kA >= SHOOTOUT_REGULATION_KICKS && kB >= SHOOTOUT_REGULATION_KICKS) {
    // Sudden death: decided only on a completed, unequal pair.
    if (kA === kB && gA !== gB) return true;
    // ⚠ THE TERMINATOR. Sudden death between two sides who both keep missing
    // never ends — MEASURED: with nothing driving the takers, the played-out
    // test sat at 0-0 through eight rounds and would have run forever. Law 10
    // bounds a real shootout because every eligible player must take before
    // anyone repeats; this is that bound, as a number. Still level here and
    // the coin toss decides it, which is what a referee ends up doing when a
    // side runs out of takers.
    return kA >= SHOOTOUT_MAX_KICKS && kB >= SHOOTOUT_MAX_KICKS && kA === kB;
  }
  // Regulation: can the side that is behind still catch up with what it has
  // left? A team that cannot is beaten and the shootout stops here.
  if (gA > gB + remainingKicks(state, 1)) return true;
  if (gB > gA + remainingKicks(state, 0)) return true;
  return kA >= SHOOTOUT_REGULATION_KICKS && kB >= SHOOTOUT_REGULATION_KICKS && gA !== gB;
}

/** Whose turn it is: the side with fewer kicks taken; ties go to first-up. */
export function shootoutNextTeam(state: WorldState): number {
  const m = state.match;
  if (m.shootoutKicksA === m.shootoutKicksB) return m.shootoutFirstTeam;
  return m.shootoutKicksA < m.shootoutKicksB ? 0 : 1;
}

/**
 * Enter the shootout. The match score is FINAL and untouched from here — the
 * shootout tally is its own pair of counters, exactly as a real result reads
 * ("2-2, 4-3 on penalties").
 */
export function beginShootout(state: WorldState, out: StepResult): void {
  const m = state.match;
  m.shootoutActive = 1;
  m.shootoutKicksA = 0;
  m.shootoutKicksB = 0;
  m.shootoutGoalsA = 0;
  m.shootoutGoalsB = 0;
  m.shootoutAttemptTicks = 0;
  // The coin toss, from the sim RNG so every peer tosses the same coin.
  m.shootoutFirstTeam = pcg32Float(state.rng) < 0.5 ? 0 : 1;

  m.phase = MatchPhase.PenaltyShootout;
  m.restartStage = RestartStage.Setup;
  m.stageTimer = SHOOTOUT_OPENING_PAUSE_S;
  m.restartTaker = -1;
  m.restartTeam = m.shootoutFirstTeam;
  m.possessionTeam = -1;
  m.advantageTicks = 0;
  m.pendingWhistleTicks = 0;
  m.pendingFoulPlayer = -1;
  m.pendingFoulSeverity = 0;

  out.events.push({
    type: 'SHOOTOUT_START',
    tick: state.tick,
    team: m.shootoutFirstTeam,
    player: -1,
  });
}

/**
 * Stage the next kick, or end the match if the shootout is over.
 *
 * The kick itself is a normal `MatchPhase.Penalty` restart — `beginRestart`
 * places the ball on the spot, `enforcePenaltyFormation` clears the box and
 * `pickRestartTaker` chooses the finisher.
 */
function stageNextKick(state: WorldState, out: StepResult): void {
  const m = state.match;
  if (shootoutDecided(state)) {
    finishShootout(state, out);
    return;
  }
  const team = shootoutNextTeam(state);
  // Both sides shoot at the SAME goal, which is what real shootouts do — the
  // referee picks one end and every kick is taken there. `defendGoalX` reads
  // the attacking direction off the half, so the spot is derived from the goal
  // this team attacks in the current half and the staging follows it.
  const sign = attackSignOf(state, team);
  const spotX = sign * (state.config.pitchLength * 0.5 - PENALTY_SPOT_DISTANCE);
  m.shootoutAttemptTicks = 0;
  beginRestart(state, MatchPhase.Penalty, team, spotX, 0);
}

/** +1 if `team` attacks the +x goal, -1 otherwise. */
function attackSignOf(state: WorldState, team: number): number {
  // The same flag ball-out.ts reads (attackerOfPositiveXGoal) — NOT a local
  // re-derivation from the half, which would disagree with it the moment the
  // ends swap and hand a taker the wrong goal.
  const teamAtPositiveX = state.teamAAttacksPositiveX ? 0 : 1;
  return team === teamAtPositiveX ? 1 : -1;
}

/** Record the outcome of the attempt that just ended and queue the next. */
export function endShootoutAttempt(state: WorldState, scored: boolean, out: StepResult): void {
  const m = state.match;
  const team = m.restartTeam;
  if (team === 0) {
    m.shootoutKicksA++;
    if (scored) m.shootoutGoalsA++;
  } else {
    m.shootoutKicksB++;
    if (scored) m.shootoutGoalsB++;
  }
  out.events.push({
    type: scored ? 'SHOOTOUT_SCORED' : 'SHOOTOUT_MISSED',
    tick: state.tick,
    team,
    player: m.restartTaker,
  });

  // Back to the wrapper phase for the between-kicks pause. The players walk
  // back to the centre circle on their own (nothing stages them here) and the
  // next kick is set up when the timer expires.
  m.phase = MatchPhase.PenaltyShootout;
  m.restartStage = RestartStage.Setup;
  m.stageTimer = SHOOTOUT_INTERVAL_S;
  m.restartTaker = -1;
  m.shootoutAttemptTicks = 0;
}

function finishShootout(state: WorldState, out: StepResult): void {
  const m = state.match;
  m.shootoutActive = 0;
  m.phase = MatchPhase.FullTime;
  m.restartTaker = -1;
  // Level at the cap: the side that kicked first takes it. Only reachable via
  // the terminator above — every ordinary finish is already unequal.
  const winner =
    m.shootoutGoalsA === m.shootoutGoalsB
      ? m.shootoutFirstTeam
      : m.shootoutGoalsA > m.shootoutGoalsB ? 0 : 1;
  out.events.push({
    type: 'SHOOTOUT_END',
    tick: state.tick,
    team: winner,
    player: -1,
  });
  out.events.push({ type: 'FULL_TIME', tick: state.tick, team: -1, player: -1 });
}

/**
 * Has the live attempt finished without a goal? Called every tick the phase is
 * `Penalty` and `shootoutActive` — the goal case is handled in ball-out.ts,
 * which sees the ball cross the line.
 *
 * Only runs once the restart is LIVE: before that the taker is still walking to
 * his mark and a resting ball is the normal state, not a missed kick.
 */
export function stepShootoutAttempt(state: WorldState, out: StepResult): void {
  const m = state.match;
  if (m.restartStage !== RestartStage.Live) return;
  m.shootoutAttemptTicks++;

  const b = state.ball;
  const halfL = state.config.pitchLength * 0.5;
  const halfW = state.config.pitchWidth * 0.5;

  // 1. Out of the field of play — wide, over, or back past the goal line.
  if (Math.abs(b.px) > halfL || Math.abs(b.pz) > halfW) {
    endShootoutAttempt(state, false, out);
    return;
  }

  // 2. The keeper has it. `possessedBy` is the sim's own answer to "who has the
  //    ball", so a save he holds ends the kick on the tick he holds it.
  const holder = b.possessedBy;
  if (holder >= 0 && holder < MAX_PLAYERS && state.players.team[holder]! !== m.restartTeam) {
    endShootoutAttempt(state, false, out);
    return;
  }

  // 3. The ball has come to rest short of the goal — the rebound off a post or
  //    a keeper's parry that dribbles away. Only counts once the taker has
  //    actually struck it: `restartKickPhase` returns to InPlay on the strike.
  const struck = m.restartKickPhase === MatchPhase.InPlay;
  const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy + b.vz * b.vz);
  if (struck && speed < SHOOTOUT_BALL_REST_SPEED && b.grounded === 1) {
    endShootoutAttempt(state, false, out);
    return;
  }

  // 4. The backstop. Nothing — a wedged ball, a taker who never shoots, a
  //    disconnected human — may hold the match open.
  if (m.shootoutAttemptTicks > SHOOTOUT_ATTEMPT_MAX_S * SIMULATION_TICK_RATE) {
    endShootoutAttempt(state, false, out);
  }
}

/**
 * The wrapper phase's own tick: count the pause down, then stage the next kick.
 * Called from `stepMatchStateMachine`'s `PenaltyShootout` branch.
 */
export function stepShootout(state: WorldState, out: StepResult): void {
  const m = state.match;
  if (m.stageTimer > 0) return;
  stageNextKick(state, out);
}
